import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
  // Prefer REACT_APP_BACKEND_URL; fall back to API_URL then production API.
baseUrl: process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_API_URL || 'https://qylmox.in/api',
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem('token');
    if (token) headers.set('x-auth-token', token);
    return headers;
  },
});

export const alarmApi = createApi({
  reducerPath: 'alarmApi',
  baseQuery,
  tagTypes: ['Alarms', 'AlarmRules', 'AlarmStats'],
  endpoints: (builder) => ({
    
    // 1. GET Alarms with filters
    getAlarms: builder.query({
      query: (params) => ({
        url: '/alarms',
        params, // status, severity, alarmType, etc.
      }),
      providesTags: ['Alarms'],
      transformResponse: (res) => res?.alarms || res?.data || [],
    }),

    // 2. GET Alarm Stats
    getAlarmStats: builder.query({
      query: (params) => ({
        url: '/alarms/stats',
        params,
      }),
      providesTags: ['AlarmStats'],
      transformResponse: (res) => res?.stats || res?.data || [],
    }),

    // 3. GET Device Alarm History
    getDeviceAlarmHistory: builder.query({
      query: ({ deviceId, limit = 50 }) => ({
        url: `/alarms/device/${deviceId}`,
        params: { limit },
      }),
      transformResponse: (res) => res?.alarms || res?.data || [],
    }),

    // 4. GET Alarm Rules
    getAlarmRules: builder.query({
      query: () => '/alarms/rules',
      providesTags: ['AlarmRules'],
      transformResponse: (res) => res?.rules || res?.data || [],
    }),

    // 5. Create Alarm Rule
    createAlarmRule: builder.mutation({
      query: (newRule) => ({
        url: '/alarms/rules',
        method: 'POST',
        body: newRule,
      }),
      invalidatesTags: ['AlarmRules'],
    }),

    // 6. Update Alarm Rule
    updateAlarmRule: builder.mutation({
      query: ({ ruleId, ...body }) => ({
        url: `/alarms/rules/${ruleId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['AlarmRules'],
    }),

    // 7. Delete Alarm Rule
    deleteAlarmRule: builder.mutation({
      query: (ruleId) => ({
        url: `/alarms/rules/${ruleId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['AlarmRules'],
    }),

    // 8. Acknowledge Alarm
    acknowledgeAlarm: builder.mutation({
      query: ({ alarmId, notes }) => ({
        url: `/alarms/${alarmId}/acknowledge`,
        method: 'PUT',
        body: { notes },
      }),
      invalidatesTags: ['Alarms', 'AlarmStats'],
    }),

    // 9. Resolve Alarm
    resolveAlarm: builder.mutation({
      query: ({ alarmId, notes }) => ({
        url: `/alarms/${alarmId}/resolve`,
        method: 'PUT',
        body: { notes },
      }),
      invalidatesTags: ['Alarms', 'AlarmStats'],
    }),

    updateAlarmRemark: builder.mutation({
      query: ({ alarmId, notes }) => ({
        url: `/alarms/${alarmId}/remark`,
        method: 'PUT',
        body: { notes },
      }),
      invalidatesTags: ['Alarms'],
    }),
  }),
});

export const {
  useGetAlarmsQuery,
  useGetAlarmStatsQuery,
  useGetDeviceAlarmHistoryQuery,
  useGetAlarmRulesQuery,
  useCreateAlarmRuleMutation,
  useUpdateAlarmRuleMutation,
  useDeleteAlarmRuleMutation,
  useAcknowledgeAlarmMutation,
  useResolveAlarmMutation,
  useUpdateAlarmRemarkMutation,
} = alarmApi;
